import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Rss, Loader2, Building2, Store, TrendingUp, Sparkles, ArrowRight, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import CompanyLogo from "@/components/CompanyLogo";

const scoreColor = (s) => (s == null ? "text-sec" : s >= 66 ? "text-emerald-400" : s >= 40 ? "text-amber-400" : "text-red-400");
const timeAgo = (iso) => {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export default function Feed() {
  const { user } = useApp();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/feed").then(({ data }) => setFeed(data.feed || [])).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Rss size={14} /> Following</span>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Your Feed</h1>
      <p className="text-sec mt-2 text-sm">One stream of the latest sentiment and updates from the businesses and companies you follow.</p>

      {!user ? (
        <div className="rud-card rounded-2xl p-8 mt-8 text-center" data-testid="feed-signin">
          <p className="text-sec">Sign in to build your feed and follow businesses & companies.</p>
          <Link to="/auth" className="btn-primary h-11 px-6 inline-flex items-center gap-2 mt-4">Sign in <ArrowRight size={16} /></Link>
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sec mt-10"><Loader2 size={16} className="animate-spin" /> Loading your feed…</div>
      ) : feed.length === 0 ? (
        <div className="rud-card rounded-2xl p-8 mt-8 text-center" data-testid="feed-empty">
          <p className="text-sec">You're not following anyone yet.</p>
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            <Link to="/businesses" className="btn-primary h-10 px-5 inline-flex items-center gap-2 text-sm"><Building2 size={15} /> Browse the Business Directory</Link>
            <Link to="/businesses" className="btn-ghost rounded-full h-10 px-5 inline-flex items-center gap-2 text-sm"><Store size={15} /> Global 1000 companies</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3 mt-8" data-testid="feed-list">
          {feed.map((item) => {
            const u = item.update;
            const score = u?.score ?? null;
            const delta = u?.delta;
            const hasShift = typeof delta === "number" && delta !== 0;
            return (
              <motion.div key={`${item.type}-${item.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="rud-card rounded-2xl p-5" data-testid={`feed-item-${item.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-elevated grid place-items-center overflow-hidden text-sec shrink-0">
                    <CompanyLogo logo={item.logo} website={item.website} name={item.name} imgClassName="w-full h-full object-contain p-1 bg-white"
                      fallback={item.type === "company" ? <Building2 size={18} /> : <Store size={18} />} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={item.link} className="font-medium hover:text-brand transition-colors">{item.name}</Link>
                      <span className="text-[10px] uppercase tracking-widest text-sec border border-rud rounded-full px-2 py-0.5">{item.type}</span>
                      {item.industry && <span className="text-xs text-brand">{item.industry}</span>}
                    </div>
                    {u ? (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <Sparkles size={13} className="text-brand" />
                          <span className="text-sec">{hasShift ? "Sentiment shifted" : "Sentiment read"}</span>
                          {score != null && <span className={`font-semibold ${scoreColor(score)}`}>{score}/100</span>}
                          {u.label && <span className="text-xs text-sec uppercase">{u.label}</span>}
                          {hasShift && (
                            <span data-testid={`feed-shift-${item.id}`} className={`inline-flex items-center gap-0.5 text-xs font-semibold rounded-full px-2 py-0.5 ${delta > 0 ? "text-emerald-400 bg-emerald-400/10" : "text-red-400 bg-red-400/10"}`}>
                              {delta > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{delta > 0 ? "+" : ""}{delta}
                              <span className="text-sec font-normal ml-1">from {u.prev_score}</span>
                            </span>
                          )}
                          {typeof delta === "number" && delta === 0 && <span className="inline-flex items-center gap-0.5 text-xs text-sec"><Minus size={12} /> no change</span>}
                          <span className="text-xs text-sec ml-auto">{timeAgo(u.created_at)}</span>
                        </div>
                        {u.headline && <p className="text-sm text-[var(--rud-text)] mt-1.5">{u.headline}</p>}
                      </div>
                    ) : (
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-sm text-sec">No updates yet — get the first live sentiment read.</span>
                        <Link to={item.link} className="text-sm text-brand flex items-center gap-1 shrink-0"><TrendingUp size={14} /> Open</Link>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
